import { useState } from 'react'
import { X, Loader2, Trash2, Lock, AlertTriangle, Package, Minus, Plus } from 'lucide-react'
import { postJson } from '../../lib/api.js'
import { useMe } from '../../lib/me.js'
import { relabelBranch } from '../../lib/ticket.js'
import { nameInitial } from '../../lib/text.js'
import {
  buildStockPayload, stockRowEditState, editBlockedReason, typeTone, fmtDateTime, rowLevel,
  TYPE_ADJUST, TYPE_CUT,
} from '../../lib/stock.js'

// One stock record — the side popup. View always; edit/delete only when
// lib/stock.js#stockRowEditState allows (today = anyone, past = Stock Manager /
// Admin, transfer halves = never here).
//
// Editable: จำนวน · หมายเหตุ. NOT the item, branch, or date — changing the item
// or branch makes it a different record, and the date is what the reports bucket
// on; a silent identity/date swap is worse than forcing a delete + re-cut. Those
// render as read-only context instead.
//
// Amount follows the same rule as the ตัด Stock drawer: the SIGN comes from the
// row's type, never the user. เบิกใช้/รับเพิ่ม edit a magnitude (≥ 1); ปรับ edits
// a signed non-zero integer. buildStockPayload re-applies the sign on save.
//
// The write is POST /stock WITH stock_id → the stack's db.add_or_edit takes the
// edit branch. Xano preserves the original created_by; the proxy stamps
// modified_by, so the audit trail survives an edit.

export default function StockRecordDrawer({ row, onClose, onChanged }) {
  const me = useMe()
  const { editable, reason } = stockRowEditState(me, row)
  const isAdjust = row?.type === TYPE_ADJUST
  const actionWordTh = isAdjust ? 'ปรับ' : row?.type === TYPE_CUT ? 'เบิก' : 'รับ'

  // ปรับ keeps the sign (either direction is a real correction); everything else
  // is edited as a magnitude and re-signed from the type on save.
  const origQty = isAdjust ? (Number(row?.N) || 0) : Math.abs(Number(row?.N) || 0)
  const [qty, setQty] = useState(origQty)
  const [remark, setRemark] = useState(row?.remark || '')
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmDel, setConfirmDel] = useState(false)
  const [error, setError] = useState(null)

  const busy = saving || deleting
  const dirty = origQty !== Number(qty) || (row?.remark || '') !== remark

  // เบิกใช้/รับเพิ่ม → magnitude ≥ 1; ปรับ → any non-zero integer.
  const qtyValid = Number.isInteger(Number(qty)) && (isAdjust ? Number(qty) !== 0 : Number(qty) >= 1)

  function bump(delta) {
    setQty((q) => {
      const v = (Number(q) || 0) + delta
      // A single existing row can't be zeroed out here: for a magnitude the minus
      // button stops at 1. ปรับ may pass through 0 into negatives.
      return isAdjust ? v : Math.max(1, v)
    })
  }

  function onQtyInput(raw) {
    if (isAdjust) {
      const cleaned = String(raw).replace(/[^0-9-]/g, '').replace(/(?!^)-/g, '')
      setQty(cleaned === '' || cleaned === '-' ? 0 : Math.trunc(Number(cleaned) || 0))
    } else {
      // Magnitude only — strip any sign; 0 allowed transiently, save gated by qtyValid.
      setQty(Math.floor(Number(String(raw).replace(/[^0-9]/g, '')) || 0))
    }
  }

  async function save() {
    setSaving(true)
    setError(null)
    try {
      // buildStockPayload re-applies the sign from `type`, so an edited เบิกใช้
      // can never flip positive. Date + branch are sent unchanged (read-only here).
      await postJson('/api/stock', buildStockPayload({
        stock_id: row.id,
        type: row.type,
        inventory_id: row.inventory_id,
        branch_id: row.branch_id,
        date: row.date,
        qty,
        remark,
      }))
      onChanged?.()
      onClose?.()
    } catch (e) {
      setError(e.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  async function remove() {
    setDeleting(true)
    setError(null)
    try {
      const res = await fetch('/api/stock', {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: row.id }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${(await res.text()) || res.statusText}`)
      onChanged?.()
      onClose?.()
    } catch (e) {
      setError(e.message || 'ลบไม่สำเร็จ')
      setDeleting(false)
      setConfirmDel(false)
    }
  }

  const n = Number(row?.N) || 0
  const level = rowLevel(row)
  const unit = row?.inventory?.unit || ''

  // Audit: created is always shown. Modified is shown only when a DIFFERENT
  // person edited the row — a never-edited row stamps modified_by == created_by,
  // so "same" means "just show created".
  const created = { name: row?.Creator, at: fmtDateTime(row?.Creation_Date) }
  const modified = { name: row?.Modifier, at: fmtDateTime(row?.Modified_Date) }
  const showModified = !!modified.name && modified.name !== created.name

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onMouseDown={() => !busy && onClose?.()} />
      <div className="relative w-full max-w-[480px] bg-slate-50 shadow-2xl flex flex-col animate-ss-slide-in">

        {/* Header carries the item identity: name + type + unit + id — so it is
            not repeated as a field below. */}
        <div className="px-5 py-3.5 bg-white border-b border-slate-200 flex items-start gap-3">
          <span className="w-9 h-9 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center shrink-0">
            <Package size={18} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[15.5px] font-semibold text-slate-900 leading-tight">
              {row?.inventory?.Name || `#${row?.inventory_id}`}
            </div>
            <div className="text-[11.5px] text-slate-400 mt-1 flex items-center gap-1.5 flex-wrap">
              <span className={`inline-block rounded-full border px-1.5 py-0.5 text-[10.5px] font-medium ${typeTone(row?.type)}`}>
                {row?.type || '—'}
              </span>
              {unit && <span>· หน่วย: {unit}</span>}
              <span>· #{row?.id}</span>
            </div>
          </div>
          <button onClick={() => !busy && onClose?.()} className="text-slate-400 hover:text-slate-600 p-1">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {!editable && (
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex gap-2.5 text-[13px] text-slate-600">
              <Lock size={15} className="shrink-0 mt-0.5 text-slate-400" />
              <span>{editBlockedReason(reason)}</span>
            </div>
          )}

          {/* สาขา — read-only (changing it makes it a different record). */}
          <Field label="สาขา">
            <span className="inline-flex items-center gap-1.5 text-[14px] text-slate-800">
              {row?.branch?.color && <span className="w-2 h-2 rounded-full" style={{ background: row.branch.color }} />}
              {relabelBranch(row?.branch?.name) || `#${row?.branch_id}`}
            </span>
          </Field>

          {/* จำนวน — stepper, same sign/limit rule as the ตัด Stock drawer. */}
          <Field label={`จำนวนที่${actionWordTh}`}>
            {editable ? (
              <>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => bump(-1)} disabled={busy}
                    className="w-8 h-8 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center justify-center disabled:opacity-40">
                    <Minus size={14} />
                  </button>
                  <input value={qty} onChange={(e) => onQtyInput(e.target.value)}
                    inputMode={isAdjust ? 'text' : 'numeric'} disabled={busy}
                    className={`w-16 h-8 rounded-lg border text-center text-[14px] font-semibold outline-none focus:ring-2 focus:ring-brand-100 ${
                      qtyValid ? 'border-slate-200 text-slate-900 focus:border-brand-300' : 'border-amber-300 text-amber-700'}`} />
                  <button onClick={() => bump(+1)} disabled={busy}
                    className="w-8 h-8 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center justify-center disabled:opacity-40">
                    <Plus size={14} />
                  </button>
                </div>
                <div className={`text-[11.5px] mt-1.5 ${qtyValid ? 'text-slate-400' : 'text-amber-600'}`}>
                  {!qtyValid
                    ? (isAdjust ? 'จำนวนต้องไม่ใช่ 0' : 'จำนวนอย่างน้อย 1')
                    : isAdjust
                      ? 'ปรับได้ทั้งค่าบวกและค่าลบ (ไม่ใช่ 0)'
                      : `บันทึกเป็น ${row?.type === TYPE_CUT ? 'ค่าลบ' : 'ค่าบวก'} อัตโนมัติ`}
                </div>
              </>
            ) : (
              <div className={`text-[14px] font-semibold ${n < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                {n > 0 ? `+${n}` : n}{unit && <span className="text-[12px] font-normal text-slate-400 ml-1">{unit}</span>}
              </div>
            )}
          </Field>

          <Field label="หมายเหตุ">
            {editable ? (
              <input value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="เพิ่มหมายเหตุ…" disabled={busy}
                className={FIELD} />
            ) : (
              <div className="text-[14px] text-slate-600">{row?.remark || '—'}</div>
            )}
          </Field>

          <Field label="คงเหลือปัจจุบัน (สาขานี้)">
            <div className={`text-[14px] tabular-nums font-medium ${level != null && level < 0 ? 'text-rose-600' : 'text-slate-800'}`}>
              {level == null ? '—' : `${level.toLocaleString('en-US')} ${unit}`}
            </div>
          </Field>

          {/* Record info — วันที่ + who logged it, grouped in one trailing section. */}
          <section className="border-t border-slate-200 pt-4 space-y-4">
            <Field label="วันที่">
              <div className="text-[14px] text-slate-800 tabular-nums">{row?.date || '—'}</div>
            </Field>
            <Field label="ผู้บันทึก">
              <AuditLine name={created.name} at={created.at} />
            </Field>
            {showModified && (
              <Field label="แก้ไขล่าสุด">
                <AuditLine name={modified.name} at={modified.at} />
              </Field>
            )}
          </section>

          {error && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 flex gap-2.5 text-[13px] text-rose-800">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {editable && (
          <div className="px-5 py-3.5 bg-white border-t border-slate-200 flex items-center gap-3">
            {confirmDel ? (
              <>
                <span className="flex-1 text-[13px] text-rose-700">ลบรายการนี้ถาวร?</span>
                <button onClick={() => setConfirmDel(false)} disabled={busy}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-[13.5px] text-slate-600 hover:bg-slate-50">
                  ยกเลิก
                </button>
                <button onClick={remove} disabled={busy}
                  className="rounded-xl bg-rose-600 px-4 py-2 text-[13.5px] font-semibold text-white hover:bg-rose-700 disabled:opacity-40 flex items-center gap-2">
                  {deleting && <Loader2 size={14} className="animate-spin" />}ลบเลย
                </button>
              </>
            ) : (
              <>
                <button onClick={() => setConfirmDel(true)} disabled={busy}
                  className="rounded-xl border border-slate-200 p-2.5 text-slate-400 hover:text-rose-600 hover:border-rose-200 disabled:opacity-40"
                  title="ลบรายการนี้">
                  <Trash2 size={16} />
                </button>
                <div className="flex-1" />
                <button onClick={() => !busy && onClose?.()} disabled={busy}
                  className="rounded-xl border border-slate-200 px-4 py-2.5 text-[14px] font-medium text-slate-600 hover:bg-slate-50">
                  ปิด
                </button>
                <button onClick={save} disabled={busy || !dirty || !qtyValid}
                  className="rounded-xl bg-brand-600 px-5 py-2.5 text-[14px] font-semibold text-white hover:bg-brand-700 disabled:opacity-40 flex items-center gap-2">
                  {saving && <Loader2 size={15} className="animate-spin" />}บันทึก
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// Field label convention shared with the sibling stock drawers (InventoryEditDrawer).
const LABEL = 'text-[12px] font-medium uppercase tracking-wide text-slate-400 mb-1.5'
const FIELD = 'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[14px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand-300 focus:ring-2 focus:ring-brand-100'

function Field({ label, children }) {
  return (
    <div>
      <div className={LABEL}>{label}</div>
      {children}
    </div>
  )
}

function AuditLine({ name, at }) {
  if (!name) return <span className="text-slate-300">—</span>
  return (
    <span className="inline-flex items-center gap-2 text-[13.5px] text-slate-600">
      <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 text-[10.5px] font-semibold flex items-center justify-center">
        {nameInitial(name)}
      </span>
      {name}
      {at && <span className="text-[11.5px] text-slate-400">· {at}</span>}
    </span>
  )
}
